import { ComponentRef, Injectable } from '@angular/core';
import {
  IDynamicItemValue,
  isIDynamicBindableValue,
  isIDynamicBindableValueCombination,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { ResolveValuesService } from '@epicuro-next/platform/dynamic-value-resolving';
import { isObservable, map, Observable, of, Subject, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ComponentsAttributesService {
  constructor(private readonly resolveValueService: ResolveValuesService) {}

  public attachAttributes(
    componentRef: ComponentRef<unknown>,
    attributes: { [key: string]: string | IDynamicItemValue },
  ): void;
  public attachAttributes(
    componentRef: ComponentRef<unknown>,
    attributes: { [key: string]: string | IDynamicItemValue },
    scope: object,
    isRoot: boolean,
  ): void;
  public attachAttributes(
    componentRef: ComponentRef<unknown>,
    attributes: { [key: string]: string | IDynamicItemValue },
    scope?: object,
    // tslint:disable-next-line:bool-param-default
    isRoot?: boolean,
  ): void {
    for (const [key, value] of Object.entries(attributes)) {
      if (typeof value === 'string') {
        this.setAttribute(componentRef, key, value);
      } else if (
        isIDynamicBindableValue(value) ||
        isIDynamicBindableValueCombination(value)
      ) {
        if (scope === undefined || isRoot === undefined) {
          throw new Error(
            'In order to resolve dynamic values you need to provide a scope and a isRoot value',
          );
        }

        this.setAttributeValueAsLongAsComponentIsAlive(
          key,
          value,
          componentRef,
          scope,
          isRoot,
        );
      }
    }
  }

  private setAttribute(
    componentRef: ComponentRef<unknown>,
    key: string,
    value: string,
  ): void {
    componentRef.location.nativeElement.setAttribute(key, value);
  }

  private getValue$(
    value: IDynamicItemValue,
    componentRef: ComponentRef<unknown>,
    scope: object,
    isRoot: boolean,
  ): Observable<unknown> {
    const resolvedValue = this.resolveValueService.resolveValue(
      value,
      componentRef,
      undefined,
      scope,
      isRoot,
    );
    let resolvedValue$: Observable<unknown>;
    if (!isObservable(resolvedValue)) {
      resolvedValue$ = of(resolvedValue);
    } else {
      resolvedValue$ = resolvedValue;
    }

    return resolvedValue$;
  }

  private mapToString(value$: Observable<unknown>): Observable<string> {
    return value$.pipe(
      map((value) => {
        let stringValue: string;
        if (typeof value !== 'string') {
          stringValue = JSON.stringify(value);
        } else {
          stringValue = value;
        }
        return stringValue;
      }),
    );
  }

  private setAttributeValueAsLongAsComponentIsAlive(
    attributeKey: string,
    value: IDynamicItemValue,
    componentRef: ComponentRef<unknown>,
    scope: object,
    isRoot: boolean,
  ) {
    const resolvedValue$ = this.mapToString(
      this.getValue$(value, componentRef, scope, isRoot),
    );

    const destroyed$ = new Subject<void>();
    componentRef.onDestroy(() => {
      destroyed$.next();
    });

    resolvedValue$.pipe(takeUntil(destroyed$)).subscribe({
      next: (resolvedValue) => {
        this.setAttribute(componentRef, attributeKey, resolvedValue);
      },
      complete: () => {
        destroyed$.complete();
      },
    });
  }
}
